import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Plus } from 'lucide-react'
import { useProjects } from '../hooks/useProjects'
import { FilterBar } from '../components/ui/FilterBar'
import { Skeleton } from '../components/ui/Skeleton'
import { EnrollProjectForm } from '../components/EnrollProjectForm'

/** Project directory list-report (UX charter: filter bar, skeleton, empty-state
 *  CTA, navigable rows, identifiers off the primary column). */
export function HomePage() {
  const { t } = useTranslation()
  const { data: projects, isLoading, isError } = useProjects()
  const [query, setQuery] = useState('')
  const [enrolling, setEnrolling] = useState(false)

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return projects ?? []
    return (projects ?? []).filter(
      (p) => p.name.toLowerCase().includes(q) || p.repoUrl.toLowerCase().includes(q),
    )
  }, [projects, query])

  return (
    <section>
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">{t('home.title')}</h2>
          <p className="mt-1 text-sm text-neutral-500">{t('home.subtitle')}</p>
        </div>
        <button
          type="button"
          onClick={() => setEnrolling((v) => !v)}
          className="flex items-center gap-1 rounded bg-neutral-900 px-3 py-2 text-sm text-white dark:bg-neutral-100 dark:text-neutral-900"
        >
          <Plus size={16} /> {t('directory.enroll')}
        </button>
      </div>

      {enrolling && (
        <div className="mt-4">
          <EnrollProjectForm onClose={() => setEnrolling(false)} />
        </div>
      )}

      <div className="mt-4 max-w-md">
        <FilterBar onChange={setQuery} placeholder={t('directory.search')} />
      </div>

      {isLoading && (
        <div className="mt-4">
          <Skeleton rows={5} columns={3} />
        </div>
      )}
      {isError && <p className="mt-6 text-red-600">{t('home.loadError')}</p>}

      {projects && projects.length === 0 && (
        <div className="mt-10 text-center">
          <p className="text-neutral-400">{t('home.empty')}</p>
          <button type="button" onClick={() => setEnrolling(true)} className="mt-3 text-sm font-medium text-blue-600">
            {t('home.emptyCta')}
          </button>
        </div>
      )}

      {projects && projects.length > 0 && (
        <table className="mt-4 w-full text-sm">
          <thead className="text-left text-xs uppercase text-neutral-500">
            <tr>
              <th className="py-2">{t('directory.name')}</th>
              <th>{t('directory.repo')}</th>
              <th>{t('directory.source')}</th>
              <th>{t('directory.status')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-200 dark:divide-neutral-800">
            {filtered.map((p) => (
              <tr key={p.id} className="hover:bg-neutral-100 dark:hover:bg-neutral-800">
                <td className="py-2">
                  <Link to={`/projects/${p.id}`} className="font-medium text-blue-600">
                    {p.name}
                  </Link>
                </td>
                <td className="text-neutral-500">{p.repoUrl}</td>
                <td className="text-neutral-500">{p.ingestionSource}</td>
                <td className="text-neutral-500">{p.status}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  )
}
